import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_VULNS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import CVSS3Calculator from './components/CVSS3Calculator';
import { Title } from '../ExpandableWrapper/styled';
import {
  Wrapper, Header, Button, Arrow, ResultsWrapper,
  Results, ResultsTitle, Score, Vector, CVSS2Title,
  IconWrapper, Remove, VectorInput, VectorError
} from './styled';
import CVSS4Calculator from './components/CVSS4Calculator';
import {
  CVSS2,
  CVSS3,
  CVSS4,
  getCvssVectors,
  isInvalidCvssVector,
  isValidCvssVector
} from './constants';

const CVSS2_EMPTY_VECTOR = 'AV:N/AC:L/Au:N/C:N/I:P/A:N';
const CVSS3_EMPTY_VECTOR = 'CVSS:3.1/AV:_/AC:_/PR:_/UI:_/S:_/C:_/I:_/A:_';
const CVSS4_EMPTY_VECTOR = 'CVSS:4.0/AV:N/AC:L/AT:N/PR:N/UI:N/VC:H/VI:H/VA:H/SC:H/SI:H/SA:H/E:A/CR:H/IR:H/AR:H/MAC:L/MAT:P/MPR:H/MUI:N/MVC:H/MVI:H/MVA:H/MSC:H/MSI:S/MSA:S';

const getVectorRows = (value = '') => Math.max(1, Math.ceil(value.length / 95));

const EditableVector = ({
  canUpdate,
  children,
  disable,
  displayValue,
  error,
  onChange,
  placeholder,
  value
}) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!canUpdate || !textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  }, [canUpdate, placeholder, value]);

  return (
    <Vector disable={ disable } editable={ canUpdate } error={ error }>
      { canUpdate
        ? <VectorInput
            ref={ textareaRef }
            rows={ getVectorRows(value || placeholder) }
            value={ value }
            placeholder={ placeholder }
            onChange={ (e) => onChange(e.target.value) }
        />
        : displayValue
      }
      { children }
    </Vector>
  );
};

const CVSS = ({ showRiskInfo, currentVuln, updateVuln, tempCVSS, resetCVSS, entity }) => {
  const [showCVSS3Calculator, toggleCVSS3Calculator] = useState(false);
  const [showCVSS4Calculator, toggleCVSS4Calculator] = useState(false);
  const { cvss2, cvss3, cvss4 } = currentVuln;
  const [manualVectors, setManualVectors] = useState(getCvssVectors(currentVuln));
  const canUpdate = useSelector((state) => selectAllowedByRole(state, FEATURE_VULNS, PERMISSION_TYPES.UPDATE));
  const dispatch = useDispatch();
  const intl = useIntl();
  const resultsText = intl.formatMessage({ id: 'manage.detail.tab.general.cvss3.results' });
  const invalidVectorText = intl.formatMessage({ id: 'manage.detail.tab.general.cvss.invalid' });
  const currentCvss2Vector = cvss2?.vector_string || '';
  const currentCvss3Vector = cvss3?.vector_string || '';
  const currentCvss4Vector = cvss4?.vector_string || '';
  const currentVulnVectors = {
    [CVSS2]: currentCvss2Vector,
    [CVSS3]: currentCvss3Vector,
    [CVSS4]: currentCvss4Vector
  };
  const shouldUseTempCVSS = showCVSS3Calculator && tempCVSS?.vector_string;
  const cvss2VectorError = isInvalidCvssVector(manualVectors[CVSS2], CVSS2);
  const cvss3VectorError = !shouldUseTempCVSS && isInvalidCvssVector(manualVectors[CVSS3], CVSS3);
  const cvss4VectorError = isInvalidCvssVector(manualVectors[CVSS4], CVSS4);

  const cvss3VectorString = () => {
    if (shouldUseTempCVSS) return tempCVSS.vector_string;
    else if (cvss3?.vector_string) return `${cvss3?.vector_string}`;
    else return CVSS3_EMPTY_VECTOR;
  };

  const cvss3Score = () => {
    if (shouldUseTempCVSS && tempCVSS.base_score) return `${tempCVSS.base_severity} (${tempCVSS.base_score})`;
    else if (cvss3?.base_score) return `${cvss3.base_severity} (${cvss3.base_score})`;
    else return 'N/A';
  };

  const cvss3Severity = () => {
    if (shouldUseTempCVSS && tempCVSS.base_severity) return tempCVSS.base_severity;
    else if (cvss3.base_severity) return cvss3.base_severity;
    else return '';
  };

  const incompleteVector = cvss3VectorString().includes('_');

  const cvss4VectorString = () => {
    if (cvss4?.vector_string) return `${cvss4.vector_string}`;
    else return CVSS4_EMPTY_VECTOR;
  };

  const cvss4Score = () => {
    if (cvss4?.base_score) return `${cvss4.base_severity} (${cvss4.base_score})`;
    else return 'N/A';
  };

  const cvss4Severity = () => {
    if (cvss4?.base_severity) return cvss4.base_severity;
    else return '';
  };

  useEffect(() => {
    toggleCVSS3Calculator(false);
    toggleCVSS4Calculator(false);
    dispatch(resetCVSS());
    setManualVectors(getCvssVectors(currentVuln));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVuln.id]);

  useEffect(() => {
    const nextServerVectors = {
      [CVSS2]: currentCvss2Vector,
      [CVSS3]: currentCvss3Vector,
      [CVSS4]: currentCvss4Vector
    };

    setManualVectors((prevVectors) => {
      const nextVectors = { ...prevVectors };
      let changed = false;

      Object.entries(nextServerVectors).forEach(([type, vector]) => {
        const shouldSyncCurrentType = !isInvalidCvssVector(prevVectors[type], type);

        if (shouldSyncCurrentType && prevVectors[type] !== vector) {
          nextVectors[type] = vector;
          changed = true;
        }
      });

      return changed ? nextVectors : prevVectors;
    });
  }, [currentCvss2Vector, currentCvss3Vector, currentCvss4Vector]);

  const removeCVSS = (field) => {
    setManualVectors((prevVectors) => ({
      ...prevVectors,
      [field]: ''
    }));
    dispatch(updateVuln(currentVuln, field, { vector_string: '' }));
    if (field === 'cvss3') {
      dispatch(resetCVSS());
      toggleCVSS3Calculator(false);
    } else if (field === 'cvss4') {
      toggleCVSS4Calculator(false);
    }
  };

  const onManualVectorChange = (type, value) => {
    setManualVectors((prevVectors) => ({
      ...prevVectors,
      [type]: value
    }));

    if (type === CVSS3) dispatch(resetCVSS());
    if (!value) {
      if (currentVulnVectors[type]) removeCVSS(type);
      return;
    }
    if (!isValidCvssVector(value, type)) return;
    if (currentVulnVectors[type] === value) return;

    dispatch(updateVuln(currentVuln, type, { vector_string: value }));
  };

  if (!showRiskInfo && !canUpdate) return null;
  return (
    <Wrapper>
      <Header>
        <Title children={ intl.formatMessage({ id: 'manage.detail.tab.general.cvss4' }) } />
        { canUpdate &&
          <Button onClick={ () => toggleCVSS4Calculator(!showCVSS4Calculator) } >
            { showCVSS4Calculator ? 'Hide Calculator' : 'Show Calculator' }
            { showCVSS4Calculator
              ? <Arrow>&#9662;</Arrow>
              : <Arrow>&#9656;</Arrow>
            }
          </Button>
        }
      </Header>
      <ResultsWrapper>
        <ResultsTitle>{ resultsText }</ResultsTitle>
        <Results>
          <Score severity={ cvss4Severity() }>{ cvss4Score() }</Score>
          <EditableVector
            canUpdate={ canUpdate }
            disable={ !currentCvss4Vector && !manualVectors[CVSS4] }
            displayValue={ cvss4VectorString() }
            error={ cvss4VectorError }
            placeholder={ CVSS4_EMPTY_VECTOR }
            value={ manualVectors[CVSS4] || '' }
            onChange={ (value) => onManualVectorChange(CVSS4, value) }
          >
            { canUpdate &&
              <IconWrapper onClick={ () => removeCVSS('cvss4') } fullVector={ cvss4?.vector_string } >
                <Remove />
              </IconWrapper>
            }
          </EditableVector>

        </Results>
        { cvss4VectorError && <VectorError>{ invalidVectorText }</VectorError> }
      </ResultsWrapper>
      { showCVSS4Calculator && <CVSS4Calculator cvss4Data={ cvss4 } entity={ entity } /> }
      <Header>
        <Title children={ intl.formatMessage({ id: 'manage.detail.tab.general.cvss3' }) } />
        { canUpdate &&
          <Button onClick={ () => toggleCVSS3Calculator(!showCVSS3Calculator) } >
            { showCVSS3Calculator ? 'Hide Calculator' : 'Show Calculator' }
            { showCVSS3Calculator
              ? <Arrow>&#9662;</Arrow>
              : <Arrow>&#9656;</Arrow>
            }
          </Button>
        }
      </Header>
      { showCVSS3Calculator && <CVSS3Calculator cvssData={ cvss3 } entity={ entity } /> }
      <ResultsWrapper>
        <ResultsTitle>{ resultsText }</ResultsTitle>
        <Results>
          <Score severity={ cvss3Severity() }>{ cvss3Score() }</Score>
          <EditableVector
            canUpdate={ canUpdate }
            disable={ !currentCvss3Vector && !shouldUseTempCVSS && !manualVectors[CVSS3] }
            displayValue={ cvss3VectorString() }
            error={ cvss3VectorError }
            placeholder={ CVSS3_EMPTY_VECTOR }
            value={ shouldUseTempCVSS ? tempCVSS.vector_string : manualVectors[CVSS3] || '' }
            onChange={ (value) => onManualVectorChange(CVSS3, value) }
          >
            { canUpdate &&
              <IconWrapper onClick={ () => removeCVSS('cvss3') } fullVector={ !incompleteVector } >
                <Remove />
              </IconWrapper>
            }
          </EditableVector>

        </Results>
        { cvss3VectorError && <VectorError>{ invalidVectorText }</VectorError> }
      </ResultsWrapper>

      <CVSS2Title children={ intl.formatMessage({ id: 'manage.detail.tab.general.cvss2' }) } />
      <ResultsWrapper>
        <ResultsTitle>{ resultsText }</ResultsTitle>
        <Results>
          <Score severity={ (cvss2.base_score && cvss2.base_severity) ? cvss2.base_severity : '' }>{ cvss2.base_score ? `${cvss2.base_severity} (${cvss2.base_score})` : 'N/A' }</Score>
          <EditableVector
            canUpdate={ canUpdate }
            disable={ !currentCvss2Vector && !manualVectors[CVSS2] }
            displayValue={ cvss2?.vector_string ? `${cvss2.vector_string}` : CVSS2_EMPTY_VECTOR }
            error={ cvss2VectorError }
            placeholder={ CVSS2_EMPTY_VECTOR }
            value={ manualVectors[CVSS2] || '' }
            onChange={ (value) => onManualVectorChange(CVSS2, value) }
          >
            { canUpdate &&
              <IconWrapper onClick={ () => removeCVSS('cvss2') } fullVector={ cvss2?.vector_string } >
                <Remove />
              </IconWrapper>
            }
          </EditableVector>
        </Results>
        { cvss2VectorError && <VectorError>{ invalidVectorText }</VectorError> }
      </ResultsWrapper>
    </Wrapper>
  );
};

export default CVSS;
